/**
 * Missions Marketing - OmniHub
 * Page dédiée à la gestion des Missions CMO
 * 
 * GOUVERNANCE:
 * Conversation → Mission (validée) → Action (validée) → Livrable → Mesure
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Target, Plus, ChevronRight, Check, Play, X, Clock,
  FileText, Download, Search, RefreshCw, Loader2,
  CheckCircle2, Circle, AlertCircle, Archive, Eye, Pause, Ticket,
  ClipboardList, User, Calendar, AlertTriangle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../../components/ui/tabs";

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Statuts et leurs styles
const STATUTS = {
  proposee: { label: "Proposée", color: "bg-blue-500/20 text-blue-400", icon: Circle },
  validee: { label: "Validée", color: "bg-amber-500/20 text-amber-400", icon: Check },
  en_cours: { label: "En cours", color: "bg-emerald-500/20 text-emerald-400", icon: Play },
  cloturee: { label: "Clôturée", color: "bg-zinc-500/20 text-zinc-400", icon: Archive },
  suspendue: { label: "Suspendue", color: "bg-amber-500/20 text-amber-400", icon: Pause },
  bloquee: { label: "Bloquée", color: "bg-red-500/20 text-red-400", icon: AlertCircle },
};

const ACTION_STATUTS = {
  proposee: { label: "Proposée", color: "bg-blue-500/20 text-blue-400" },
  validee: { label: "Validée", color: "bg-amber-500/20 text-amber-400" },
  en_cours: { label: "En cours", color: "bg-purple-500/20 text-purple-400" },
  executee: { label: "Exécutée", color: "bg-emerald-500/20 text-emerald-400" },
  echouee: { label: "Échouée", color: "bg-red-500/20 text-red-400" },
  bloquee: { label: "Bloquée", color: "bg-red-500/20 text-red-400" },
  brief_genere: { label: "Brief généré", color: "bg-amber-500/20 text-amber-400" },
  annulee: { label: "Annulée", color: "bg-zinc-500/20 text-zinc-400" },
};

const ACTION_TYPES = [
  { value: "audit_seo", label: "Audit SEO" },
  { value: "analyse_opportunites", label: "Analyse Opportunités" },
  { value: "plan_actions_seo", label: "Plan d'Actions SEO" },
  { value: "creation_contenu", label: "Création de Contenu" },
  { value: "brief_contenu", label: "Brief Contenu" },
  { value: "analyse_concurrence", label: "Analyse Concurrence" },
  { value: "rapport_performance", label: "Rapport Performance" },
];

// ============================================================================
// COMPOSANT CARTE MISSION
// ============================================================================
const MissionCard = ({ mission, onClick, onValidate, onStart, onArchive, onViewTicket }) => {
  const statut = STATUTS[mission.statut] || STATUTS.proposee;
  const StatusIcon = statut.icon;
  const isSuspended = mission.statut === "suspendue" || mission.statut === "bloquee";

  return (
    <div
      onClick={onClick}
      className={`p-4 rounded-lg border transition-all cursor-pointer group ${
        isSuspended 
          ? 'bg-amber-500/5 border-amber-500/30 hover:border-amber-500/50' 
          : 'bg-zinc-900/50 border-white/5 hover:border-white/10'
      }`}
    >
      {/* Bandeau Mission Suspendue avec lien Ticket */}
      {isSuspended && (mission.ticket_id || mission.decision_ticket) && (
        <div 
          className="flex items-center justify-between mb-3 px-3 py-2 bg-amber-500/10 rounded-lg border border-amber-500/20"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2">
            <Ticket className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-amber-400 font-medium">
              Mission suspendue — Ticket CxO #{String(mission.ticket_numero || mission.decision_ticket?.ticket_numero || '?').padStart(3, '0')}
            </span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const ticketId = mission.ticket_id || mission.decision_ticket?.ticket_id;
              if (onViewTicket && ticketId) onViewTicket(ticketId);
            }}
            className="text-xs text-amber-400 hover:text-amber-300 underline flex items-center gap-1"
          >
            Voir le ticket
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
      
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <StatusIcon className="w-4 h-4 text-zinc-400" />
            <Badge className={statut.color}>{statut.label}</Badge>
            {mission.origine === "cmo_ia" && (
              <Badge className="bg-purple-500/20 text-purple-400 text-xs">CMO IA</Badge>
            )}
            <span className="text-xs text-zinc-500 font-mono">{mission.id}</span>
          </div>
          <h3 className="text-white font-medium text-lg mb-1">{mission.titre}</h3>
          <p className="text-zinc-400 text-sm line-clamp-2">{mission.objectif}</p>
          
          <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
            {mission.periode_debut && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(mission.periode_debut).toLocaleDateString("fr-FR")}
                {mission.periode_fin && ` → ${new Date(mission.periode_fin).toLocaleDateString("fr-FR")}`}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Target className="w-3 h-3" />
              {mission.actions_count || 0} action(s)
            </span>
            <span className="flex items-center gap-1">
              <FileText className="w-3 h-3" />
              {mission.livrables_count || 0} livrable(s)
            </span>
          </div>
        </div>
        
        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
          {mission.statut === "proposee" && (
            <Button
              size="sm"
              variant="outline"
              className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
              onClick={() => onValidate(mission.id)}
              title="Valider"
            >
              <Check className="w-4 h-4" />
            </Button>
          )}
          {mission.statut === "validee" && (
            <Button
              size="sm"
              variant="outline"
              className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
              onClick={() => onStart(mission.id)}
              title="Démarrer"
            >
              <Play className="w-4 h-4" />
            </Button>
          )}
          {/* Bouton archiver */}
          <Button
            size="sm"
            variant="ghost"
            className="text-zinc-500 hover:text-zinc-300"
            onClick={() => onArchive(mission.id)}
            title="Archiver"
          >
            <Archive className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// DIALOG NOUVELLE MISSION
// ============================================================================
const NewMissionDialog = ({ open, onClose, onSave }) => {
  const [form, setForm] = useState({
    titre: "",
    objectif: "",
    periode_debut: "",
    periode_fin: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.titre || !form.objectif) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        origine: "utilisateur",
        cxo_type: "cmo",
      };
      if (payload.periode_debut) payload.periode_debut = new Date(payload.periode_debut).toISOString();
      if (payload.periode_fin) payload.periode_fin = new Date(payload.periode_fin).toISOString();

      await axios.post(`${API}/api/marketing/missions`, payload, { headers: getAuthHeaders() });
      onSave();
      onClose();
      setForm({ titre: "", objectif: "", periode_debut: "", periode_fin: "" });
    } catch (err) {
      alert("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-400" />
            Nouvelle Mission
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-zinc-300">Titre *</Label>
            <Input
              value={form.titre}
              onChange={(e) => setForm({ ...form, titre: e.target.value })}
              placeholder="Ex: Améliorer le SEO de TEF"
              className="bg-zinc-800 border-white/10 text-white"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300">Objectif (clair et mesurable) *</Label>
            <Textarea
              value={form.objectif}
              onChange={(e) => setForm({ ...form, objectif: e.target.value })}
              placeholder="Ex: Améliorer la position moyenne de 5 places sur les requêtes cibles en 3 mois"
              className="bg-zinc-800 border-white/10 text-white min-h-[80px]"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Période début</Label>
              <Input
                type="date"
                value={form.periode_debut}
                onChange={(e) => setForm({ ...form, periode_debut: e.target.value })}
                className="bg-zinc-800 border-white/10 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Période fin</Label>
              <Input
                type="date"
                value={form.periode_fin}
                onChange={(e) => setForm({ ...form, periode_fin: e.target.value })}
                className="bg-zinc-800 border-white/10 text-white"
              />
            </div>
          </div>

          <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p className="text-blue-300 text-sm">
              📋 La mission sera créée avec le statut <strong>Proposée</strong>. 
              Vous devrez la valider avant de pouvoir y ajouter des actions.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={handleSubmit}
            disabled={!form.titre || !form.objectif || saving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Créer la Mission
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// DIALOG DÉTAIL MISSION
// ============================================================================
const MissionDetailDialog = ({ open, onClose, missionId, onRefresh }) => {
  const [mission, setMission] = useState(null);
  const [actions, setActions] = useState([]);
  const [livrables, setLivrables] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(null);
  const [showNewAction, setShowNewAction] = useState(false);
  const [newAction, setNewAction] = useState({ type: "audit_seo", description: "", site_code: "TEF" });
  const [viewingLivrable, setViewingLivrable] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const loadMission = useCallback(async () => {
    if (!missionId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/marketing/missions/${missionId}`, { headers: getAuthHeaders() });
      setMission(res.data.mission);
      setActions(res.data.actions || []);
      setLivrables(res.data.livrables || []);
      
      // Charger les briefs humains pour cette mission
      try {
        const briefsRes = await axios.get(`${API}/api/marketing/briefs?mission_id=${missionId}`, { headers: getAuthHeaders() });
        setBriefs(briefsRes.data.briefs || []);
      } catch (briefErr) {
        console.log("Pas de briefs pour cette mission");
        setBriefs([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [missionId]);

  useEffect(() => {
    if (open && missionId) {
      loadMission();
    }
  }, [open, missionId, loadMission]);

  // Télécharger un livrable
  const handleDownloadLivrable = async (livrable) => {
    setDownloading(livrable.id);
    try {
      const res = await axios.get(
        `${API}/api/marketing/missions/livrables/${livrable.id}/download`,
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', livrable.nom || 'livrable.md');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erreur téléchargement:", err);
      // Fallback: ouvrir le contenu si disponible
      if (livrable.contenu) {
        setViewingLivrable(livrable);
      } else {
        alert("Erreur lors du téléchargement.");
      }
    } finally {
      setDownloading(null);
    }
  };

  const handleValidateMission = async () => {
    try {
      await axios.post(`${API}/api/marketing/missions/${missionId}/validate`, {}, { headers: getAuthHeaders() });
      loadMission();
      onRefresh();
    } catch (err) {
      alert("Erreur: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleStartMission = async () => {
    try {
      await axios.post(`${API}/api/marketing/missions/${missionId}/start`, {}, { headers: getAuthHeaders() });
      loadMission();
      onRefresh();
    } catch (err) {
      alert("Erreur: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleCloseMission = async () => {
    try {
      await axios.post(`${API}/api/marketing/missions/${missionId}/close`, {}, { headers: getAuthHeaders() });
      loadMission();
      onRefresh();
    } catch (err) {
      alert("Erreur: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleCreateAction = async () => {
    if (!newAction.type || !newAction.description) return;
    try {
      await axios.post(`${API}/api/marketing/missions/${missionId}/actions`, {
        ...newAction,
        params: { site_code: newAction.site_code },
        cxo_type: "cmo"
      }, { headers: getAuthHeaders() });
      setShowNewAction(false);
      setNewAction({ type: "audit_seo", description: "", site_code: "TEF" });
      loadMission();
    } catch (err) {
      alert("Erreur: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleValidateAction = async (actionId) => {
    try {
      await axios.post(`${API}/api/marketing/missions/actions/${actionId}/validate`, {}, { headers: getAuthHeaders() });
      loadMission();
    } catch (err) {
      alert("Erreur: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleExecuteAction = async (actionId) => {
    setExecuting(actionId);
    try {
      await axios.post(`${API}/api/marketing/missions/actions/${actionId}/execute`, {}, { headers: getAuthHeaders() });
      loadMission();
      onRefresh();
    } catch (err) {
      alert("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setExecuting(null);
    }
  };

  if (!open) return null;

  const statut = mission ? STATUTS[mission.statut] : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-white/10 max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-400" />
            {loading ? "Chargement..." : mission?.titre}
            {statut && <Badge className={statut.color}>{statut.label}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
          </div>
        ) : mission ? (
          <div className="overflow-y-auto flex-1 space-y-6 py-4 pr-4">
            {/* CONTEXTE DE BLOCAGE - Affiché en premier si mission bloquée */}
            {mission.statut === "bloquee" && (
              <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/30 space-y-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-red-400 font-semibold text-lg">Mission Bloquée</h4>
                    <p className="text-zinc-300 mt-1">{mission.blocage_raison || "Moyens insuffisants pour atteindre l'objectif"}</p>
                  </div>
                </div>
                
                {/* Moyens requis */}
                {mission.moyens_requis?.length > 0 && (
                  <div className="bg-zinc-900/50 p-3 rounded-lg">
                    <p className="text-sm text-zinc-400 mb-2">📋 Moyens requis pour débloquer:</p>
                    <div className="space-y-2">
                      {mission.moyens_requis.map((moyen, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            moyen.urgence === 'critique' ? 'bg-red-500/20 text-red-400' :
                            moyen.urgence === 'haute' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-zinc-500/20 text-zinc-400'
                          }`}>
                            {moyen.urgence || 'requis'}
                          </span>
                          <div className="flex-1">
                            <span className="text-white font-medium">{moyen.type}</span>
                            <p className="text-sm text-zinc-400">{moyen.description}</p>
                            {moyen.budget_estime && (
                              <p className="text-sm text-emerald-400">Budget: {moyen.budget_estime}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Options décisionnelles */}
                <div className="border-t border-red-500/20 pt-4">
                  <p className="text-sm text-zinc-400 mb-3">🎯 Options décisionnelles:</p>
                  <div className="grid gap-2">
                    <Button 
                      className="w-full bg-emerald-600 hover:bg-emerald-700 justify-start"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/api/hub/cmo/blocked-missions/${mission.id}/unblock`, null, {
                            headers: getAuthHeaders(),
                            params: { decision: "Budget validé - moyens accordés" }
                          });
                          alert("Mission débloquée - Le Worker reprendra l'exécution");
                          onOpenChange(false);
                        } catch (err) { alert("Erreur: " + err.message); }
                      }}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      ✅ Valider les moyens demandés et débloquer
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10 justify-start"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/api/hub/cmo/blocked-missions/${mission.id}/unblock`, null, {
                            headers: getAuthHeaders(),
                            params: { decision: "Continuer avec moyens actuels (SEO uniquement)" }
                          });
                          alert("Mission débloquée - Continuation avec moyens actuels");
                          onOpenChange(false);
                        } catch (err) { alert("Erreur: " + err.message); }
                      }}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      ⚠️ Continuer avec les moyens actuels (objectif peut ne pas être atteint)
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 justify-start"
                      onClick={async () => {
                        try {
                          await axios.patch(`${API}/api/marketing/missions/${mission.id}`, {
                            statut: "cloturee"
                          }, { headers: getAuthHeaders() });
                          alert("Mission clôturée");
                          onOpenChange(false);
                        } catch (err) { alert("Erreur: " + err.message); }
                      }}
                    >
                      <X className="w-4 h-4 mr-2" />
                      ❌ Abandonner cette mission
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* MISSION SUSPENDUE - Affiche le lien vers le ticket CxO */}
            {mission.statut === "suspendue" && mission.ticket_id && (
              <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30 space-y-3">
                <div className="flex items-start gap-3">
                  <Ticket className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-amber-400 font-semibold text-lg">Mission Suspendue</h4>
                    <p className="text-zinc-300 mt-1">
                      Cette mission est en attente d&apos;une décision. Un ticket CxO a été créé.
                    </p>
                    <div className="mt-3 p-3 bg-zinc-900/50 rounded-lg flex items-center justify-between">
                      <div>
                        <p className="text-xs text-zinc-500">Ticket de décision</p>
                        <p className="text-white font-medium">
                          #{String(mission.ticket_numero || 0).padStart(3, '0')}
                        </p>
                      </div>
                      <Button 
                        size="sm"
                        className="bg-amber-500 hover:bg-amber-600 text-black"
                        onClick={() => {
                          // Rediriger vers Operation Hub > Tickets CxO
                          // Dispatch un event custom pour navigation OmniHub
                          window.dispatchEvent(new CustomEvent('omnihub:navigate', { 
                            detail: { hub: 'operation', item: 'tickets-cxo' }
                          }));
                          onClose();
                        }}
                        data-testid="view-ticket-btn"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Voir le ticket
                      </Button>
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">
                      Suspendue le {mission.date_suspension ? new Date(mission.date_suspension).toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      }) : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Objectif */}
            <div className="p-4 bg-zinc-800/50 rounded-lg border border-white/5">
              <h4 className="text-zinc-400 text-sm mb-2">Objectif</h4>
              <p className="text-white">{mission.objectif}</p>
            </div>

            {/* Boutons d'action */}
            <div className="flex gap-2 flex-wrap">
              {mission.statut === "proposee" && (
                <Button onClick={handleValidateMission} className="bg-emerald-600 hover:bg-emerald-700">
                  <Check className="w-4 h-4 mr-2" /> Valider la Mission
                </Button>
              )}
              {mission.statut === "validee" && (
                <Button onClick={handleStartMission} className="bg-blue-600 hover:bg-blue-700">
                  <Play className="w-4 h-4 mr-2" /> Démarrer
                </Button>
              )}
              {mission.statut === "en_cours" && (
                <Button onClick={handleCloseMission} variant="outline">
                  <Archive className="w-4 h-4 mr-2" /> Clôturer
                </Button>
              )}
            </div>

            {/* Tabs Actions / Livrables / Briefs */}
            <Tabs defaultValue="actions" className="w-full">
              <TabsList className="bg-zinc-800 border-white/10">
                <TabsTrigger value="actions" className="data-[state=active]:bg-zinc-700">
                  Actions ({actions.length})
                </TabsTrigger>
                <TabsTrigger value="livrables" className="data-[state=active]:bg-zinc-700">
                  Livrables ({livrables.length})
                </TabsTrigger>
                {briefs.length > 0 && (
                  <TabsTrigger value="briefs" className="data-[state=active]:bg-zinc-700">
                    <ClipboardList className="w-4 h-4 mr-1" />
                    Briefs ({briefs.length})
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="actions" className="space-y-4 mt-4">
                {/* Bouton nouvelle action */}
                {["validee", "en_cours"].includes(mission.statut) && (
                  <div>
                    {showNewAction ? (
                      <div className="p-4 bg-zinc-800/50 rounded-lg border border-white/10 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-zinc-300">Type d&apos;action</Label>
                            <Select value={newAction.type} onValueChange={(v) => setNewAction({ ...newAction, type: v })}>
                              <SelectTrigger className="bg-zinc-800 border-white/10 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-800 border-white/10">
                                {ACTION_TYPES.map((t) => (
                                  <SelectItem key={t.value} value={t.value} className="text-white">
                                    {t.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-zinc-300">Code site</Label>
                            <Input
                              value={newAction.site_code}
                              onChange={(e) => setNewAction({ ...newAction, site_code: e.target.value })}
                              placeholder="TEF"
                              className="bg-zinc-800 border-white/10 text-white"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Description</Label>
                          <Textarea
                            value={newAction.description}
                            onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
                            placeholder="Décrivez l'action..."
                            className="bg-zinc-800 border-white/10 text-white"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={handleCreateAction} disabled={!newAction.description} className="bg-blue-600">
                            <Plus className="w-4 h-4 mr-2" /> Créer
                          </Button>
                          <Button variant="outline" onClick={() => setShowNewAction(false)}>
                            Annuler
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button variant="outline" onClick={() => setShowNewAction(true)}>
                        <Plus className="w-4 h-4 mr-2" /> Nouvelle Action
                      </Button>
                    )}
                  </div>
                )}

                {/* Liste des actions */}
                {actions.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Aucune action pour cette mission</p>
                    {mission.statut === "proposee" && (
                      <p className="text-sm mt-1">Validez la mission pour ajouter des actions</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {actions.map((action) => {
                      const actionStatut = ACTION_STATUTS[action.statut];
                      return (
                        <div
                          key={action.id}
                          className="p-4 bg-zinc-800/50 rounded-lg border border-white/5"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={actionStatut?.color}>{actionStatut?.label}</Badge>
                                <span className="text-zinc-500 text-xs">
                                  {ACTION_TYPES.find((t) => t.value === action.type)?.label || action.type}
                                </span>
                              </div>
                              <p className="text-white">{action.description}</p>
                              {action.params?.site_code && (
                                <span className="text-xs text-zinc-500 mt-1 inline-block">
                                  Site: {action.params.site_code}
                                </span>
                              )}
                              {action.error && (
                                <p className="text-red-400 text-sm mt-2">❌ {action.error}</p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              {action.statut === "proposee" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-amber-500/30 text-amber-400"
                                  onClick={() => handleValidateAction(action.id)}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                              )}
                              {action.statut === "validee" && (
                                <Button
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                  onClick={() => handleExecuteAction(action.id)}
                                  disabled={executing === action.id}
                                >
                                  {executing === action.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Play className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                              {action.statut === "executee" && action.livrable_id && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-blue-500/30 text-blue-400"
                                  onClick={() => {
                                    // Trouver le livrable correspondant et l'ouvrir
                                    const livrable = livrables.find(l => l.id === action.livrable_id);
                                    if (livrable) {
                                      setViewingLivrable(livrable);
                                    } else {
                                      // Charger le livrable si pas dans la liste
                                      setActiveTab("livrables");
                                    }
                                  }}
                                  title="Voir le livrable"
                                >
                                  <FileText className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="livrables" className="space-y-4 mt-4">
                {livrables.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Aucun livrable généré</p>
                    <p className="text-sm mt-1">Les livrables apparaîtront après exécution des actions</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {livrables.map((livrable) => (
                      <div
                        key={livrable.id}
                        className="p-4 bg-zinc-800/50 rounded-lg border border-white/5 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-blue-400" />
                          <div>
                            <p className="text-white font-medium">{livrable.nom}</p>
                            <p className="text-zinc-500 text-xs">
                              {livrable.zoho_path || "Non archivé"}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {livrable.contenu && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setViewingLivrable(livrable)}
                              title="Voir le contenu"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          {livrable.zoho_path && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="border-emerald-500/30 text-emerald-400"
                              onClick={() => handleDownloadLivrable(livrable)}
                              disabled={downloading === livrable.id}
                              title="Télécharger depuis Zoho"
                            >
                              {downloading === livrable.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Onglet Briefs Humains */}
              <TabsContent value="briefs" className="space-y-4 mt-4">
                {briefs.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Aucun brief humain pour cette mission</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {briefs.map((brief) => {
                      const priorityColors = {
                        critique: "border-red-500/30 bg-red-500/5",
                        haute: "border-amber-500/30 bg-amber-500/5",
                        moyenne: "border-blue-500/30 bg-blue-500/5",
                        basse: "border-zinc-500/30 bg-zinc-500/5"
                      };
                      const statusColors = {
                        en_attente: "bg-amber-500/20 text-amber-400",
                        en_cours: "bg-blue-500/20 text-blue-400",
                        complete: "bg-emerald-500/20 text-emerald-400",
                        bloque: "bg-red-500/20 text-red-400"
                      };
                      const priorityColor = priorityColors[brief.priorite] || priorityColors.moyenne;
                      const statusColor = statusColors[brief.statut] || statusColors.en_attente;

                      return (
                        <div
                          key={brief.id}
                          className={`p-4 rounded-lg border ${priorityColor}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge className={statusColor}>
                                  {brief.statut?.replace('_', ' ') || 'en attente'}
                                </Badge>
                                {brief.priorite === "critique" && (
                                  <Badge className="bg-red-500/20 text-red-400">
                                    <AlertTriangle className="w-3 h-3 mr-1" />
                                    Critique
                                  </Badge>
                                )}
                              </div>
                              <h4 className="text-white font-medium mb-1">{brief.titre}</h4>
                              <p className="text-zinc-400 text-sm mb-3">{brief.raison_non_executable}</p>
                              
                              {/* Contexte */}
                              {brief.contexte && (
                                <div className="bg-zinc-800/50 rounded-lg p-3 mb-3">
                                  <p className="text-xs text-zinc-500 mb-1">Contexte :</p>
                                  <p className="text-sm text-zinc-300">{brief.contexte}</p>
                                </div>
                              )}

                              {/* Checklist */}
                              {brief.checklist && brief.checklist.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-xs text-zinc-500 mb-2">Checklist :</p>
                                  <ul className="space-y-1">
                                    {brief.checklist.map((item, idx) => (
                                      <li key={idx} className="flex items-start gap-2 text-sm">
                                        <Circle className="w-3 h-3 text-zinc-500 mt-1 flex-shrink-0" />
                                        <span className="text-zinc-300">{item}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Critères de succès */}
                              {brief.criteres_succes && brief.criteres_succes.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-xs text-zinc-500 mb-2">Critères de succès :</p>
                                  <ul className="space-y-1">
                                    {brief.criteres_succes.map((critere, idx) => (
                                      <li key={idx} className="flex items-start gap-2 text-sm">
                                        <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-1 flex-shrink-0" />
                                        <span className="text-zinc-300">{critere}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Métadonnées */}
                              <div className="flex items-center gap-4 text-xs text-zinc-500">
                                {brief.delai_suggere && (
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    Délai : {brief.delai_suggere}
                                  </span>
                                )}
                                {brief.assigned_to && (
                                  <span className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {brief.assigned_to}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        ) : null}

        <DialogFooter className="flex-shrink-0 border-t border-white/10 pt-4">
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>

      {/* Modal visualisation du livrable */}
      <Dialog open={!!viewingLivrable} onOpenChange={() => setViewingLivrable(null)}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-400" />
              {viewingLivrable?.nom || "Livrable"}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {viewingLivrable?.zoho_path || "Contenu du livrable"}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-auto max-h-[50vh] bg-zinc-800/50 rounded-lg p-4 border border-white/10">
            <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-mono">
              {viewingLivrable?.contenu || "Aucun contenu disponible"}
            </pre>
          </div>
          <DialogFooter>
            {viewingLivrable?.zoho_path && (
              <Button 
                variant="outline" 
                className="border-emerald-500/30 text-emerald-400"
                onClick={() => handleDownloadLivrable(viewingLivrable)}
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger
              </Button>
            )}
            <Button variant="outline" onClick={() => setViewingLivrable(null)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

// ============================================================================
// PAGE PRINCIPALE
// ============================================================================
export default function MissionsMarketingPage({ embedded = false }) {
  const [missions, setMissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatut, setFilterStatut] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [selectedMissionId, setSelectedMissionId] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatut && filterStatut !== "all") params.append("statut", filterStatut);
      if (showArchived) params.append("include_archived", "true");

      const [missionsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/marketing/missions?${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/marketing/missions/stats/summary`, { headers: getAuthHeaders() }),
      ]);

      setMissions(missionsRes.data.missions || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error("Erreur:", err);
    } finally {
      setLoading(false);
    }
  }, [filterStatut, showArchived]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleValidate = async (missionId) => {
    try {
      await axios.post(`${API}/api/marketing/missions/${missionId}/validate`, {}, { headers: getAuthHeaders() });
      loadData();
    } catch (err) {
      alert("Erreur: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleStart = async (missionId) => {
    try {
      await axios.post(`${API}/api/marketing/missions/${missionId}/start`, {}, { headers: getAuthHeaders() });
      loadData();
    } catch (err) {
      alert("Erreur: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleArchive = async (missionId) => {
    try {
      await axios.post(`${API}/api/marketing/missions/${missionId}/archive`, {}, { headers: getAuthHeaders() });
      loadData();
    } catch (err) {
      alert("Erreur: " + (err.response?.data?.detail || err.message));
    }
  };

  // Filtrer par recherche
  const filteredMissions = missions.filter((m) =>
    m.titre?.toLowerCase().includes(search.toLowerCase()) ||
    m.objectif?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="missions-marketing-page">
      {/* Header - masqué en mode embedded */}
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Target className="w-6 h-6 text-blue-400" />
              Missions Marketing
            </h1>
            <p className="text-zinc-400">
              Gouvernance CMO : Mission → Action → Livrable → Mesure
            </p>
          </div>
          <Button
            onClick={() => setShowNewDialog(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="new-mission-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Mission
          </Button>
        </div>
      )}

      {/* En mode embedded, juste le bouton nouvelle mission */}
      {embedded && (
        <div className="flex items-center justify-end">
          <Button
            onClick={() => setShowNewDialog(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="new-mission-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Mission
          </Button>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="p-4 bg-zinc-900/50 rounded-lg border border-white/5">
            <p className="text-zinc-500 text-xs">Proposées</p>
            <p className="text-2xl font-bold text-blue-400">{stats.missions?.proposees || 0}</p>
          </div>
          <div className="p-4 bg-zinc-900/50 rounded-lg border border-white/5">
            <p className="text-zinc-500 text-xs">Validées</p>
            <p className="text-2xl font-bold text-amber-400">{stats.missions?.validées || 0}</p>
          </div>
          <div className="p-4 bg-zinc-900/50 rounded-lg border border-white/5">
            <p className="text-zinc-500 text-xs">En cours</p>
            <p className="text-2xl font-bold text-emerald-400">{stats.missions?.en_cours || 0}</p>
          </div>
          <div className="p-4 bg-zinc-900/50 rounded-lg border border-white/5">
            <p className="text-zinc-500 text-xs">Actions exécutées</p>
            <p className="text-2xl font-bold text-purple-400">{stats.actions?.executees || 0}</p>
          </div>
          <div className="p-4 bg-zinc-900/50 rounded-lg border border-white/5">
            <p className="text-zinc-500 text-xs">Livrables</p>
            <p className="text-2xl font-bold text-white">{stats.livrables?.total || 0}</p>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une mission..."
              className="pl-10 bg-zinc-800 border-white/10 text-white"
            />
          </div>
        </div>
        <Select value={filterStatut} onValueChange={setFilterStatut}>
          <SelectTrigger className="w-full max-w-[150px] bg-zinc-800 border-white/10 text-white">
            <SelectValue placeholder="Tous statuts" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-white/10">
            <SelectItem value="all" className="text-zinc-400">Tous</SelectItem>
            {Object.entries(STATUTS).map(([key, val]) => (
              <SelectItem key={key} value={key} className="text-white">
                {val.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Toggle Archives */}
        <Button 
          variant={showArchived ? "default" : "outline"} 
          onClick={() => setShowArchived(!showArchived)}
          className={showArchived ? "bg-zinc-700" : ""}
        >
          <Archive className="w-4 h-4 mr-2" />
          {showArchived ? "Masquer archives" : "Voir archives"}
        </Button>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Liste des missions */}
      {filteredMissions.length === 0 ? (
        <div className="text-center py-12">
          <Target className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">Aucune mission</p>
          <p className="text-zinc-500 text-sm mt-1">
            {showArchived ? "Aucune mission archivée" : "Le CMO IA créera automatiquement des missions"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredMissions.map((mission) => (
            <MissionCard
              key={mission.id}
              mission={mission}
              onClick={() => setSelectedMissionId(mission.id)}
              onValidate={handleValidate}
              onStart={handleStart}
              onArchive={handleArchive}
              onViewTicket={(ticketId) => {
                // Navigation vers le ticket via event
                localStorage.setItem('navigate_to_ticket', ticketId);
                window.dispatchEvent(new CustomEvent('navigate-to-ticket', { 
                  detail: { ticketId } 
                }));
              }}
            />
          ))}
        </div>
      )}

      {/* Dialogs */}
      <NewMissionDialog
        open={showNewDialog}
        onClose={() => setShowNewDialog(false)}
        onSave={loadData}
      />
      <MissionDetailDialog
        open={!!selectedMissionId}
        onClose={() => setSelectedMissionId(null)}
        missionId={selectedMissionId}
        onRefresh={loadData}
      />
    </div>
  );
}
